import { Link, useLocation } from 'react-router-dom';
import { useContext, useState, useEffect } from 'react';
import { Container, Grid, Button } from '@mui/material';
import _ from 'lodash';
import UserContext from '../context/userContext';
import { BASE_ROUTE } from '../api';
import style from './Header.module.css';

interface MenuItemProps {
    name: string;
    link: string;
    active?: boolean;
    useDjangoRouter?: boolean;
}

function MenuItem({ name, link, active = false, useDjangoRouter = false }: MenuItemProps) {
    if (useDjangoRouter) {
        return (
            <Grid item className={active ? style.activeMenuItem : ''}>
                <Button color="inherit" onClick={() => window.open(link, '_self')}>{name}</Button>
            </Grid>
        );
    }
    return (
        <Grid item className={active ? style.activeMenuItem : ''}>
            <Link to={link} className={style.linkItem}>
                <Button color="inherit">{name}</Button>
            </Link>
        </Grid>
    );
}

function Header() {
    const { userData, roles } = useContext(UserContext);
    const path = useLocation();
    let { pathname } = path;
    if (pathname.endsWith('/')) pathname = pathname.slice(0, -1);

    const [mobileOpen, setMobileOpen] = useState(false);

    // Close the mobile menu on navigation.
    useEffect(() => { setMobileOpen(false); }, [pathname]);

    // Lock body scroll while the mobile menu is open.
    useEffect(() => {
        if (mobileOpen) {
            const prev = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => { document.body.style.overflow = prev; };
        }
    }, [mobileOpen]);

    const items = (
        <>
            <MenuItem key={-1} name="Intranet" link={`${BASE_ROUTE}/`}     active={pathname.endsWith(BASE_ROUTE)} />
            <MenuItem key={-2} name="Nyheter"  link={`${BASE_ROUTE}/posts/`} active={pathname.includes(`${BASE_ROUTE}/posts`)} />
            <MenuItem key={-4} name="HMS"      link={`${BASE_ROUTE}/hms`}    active={pathname.includes(`${BASE_ROUTE}/hms`)} />
            <MenuItem key={-5} name="I tall"   link={`${BASE_ROUTE}/tall`}   active={pathname.includes(`${BASE_ROUTE}/tall`)} />
            <MenuItem key={-6} name="HR"       link={`${BASE_ROUTE}/hr`}     active={pathname.includes(`${BASE_ROUTE}/hr`)} />
            <MenuItem key={-7} name="KI"       link={`${BASE_ROUTE}/ai`}     active={pathname.includes(`${BASE_ROUTE}/ai`)} />
            <MenuItem key={-8} name="Profeten" link={`${BASE_ROUTE}/profeten`} active={pathname.includes(`${BASE_ROUTE}/profeten`)} />
            {userData?.menu_links?.map((item, index) => (
                <MenuItem key={index} {...item} useDjangoRouter={true} />
            ))}
            {roles.isAdmin && (
                <MenuItem key={-3} name="Innstillinger" link={`${BASE_ROUTE}/settings/general`} active={pathname.includes(`${BASE_ROUTE}/settings`)} />
            )}
        </>
    );

    return (
        <div className={style.wrapper}>
            <Container maxWidth={false}>
                <Grid container alignItems="center" className={style.headerRow}>
                    <Grid item className={style.logoCell}>
                        <Link to={`${BASE_ROUTE}/`}>
                            <img
                                src={_.get(userData, 'company.logo_path')}
                                alt="Brynild"
                                className={style.logo}
                            />
                        </Link>
                    </Grid>
                    <Grid item className={style.navCell}>
                        <Grid container justifyContent="center" spacing={5} className={style.menuItems}>
                            {items}
                        </Grid>
                    </Grid>
                    <button
                        type="button"
                        className={`${style.hamburger} ${mobileOpen ? style.hamburgerOpen : ''}`}
                        onClick={() => setMobileOpen((o) => !o)}
                        aria-label="Meny"
                        aria-expanded={mobileOpen}
                        aria-controls="mobile-nav"
                    >
                        <span />
                        <span />
                        <span />
                    </button>
                </Grid>
            </Container>

            {mobileOpen && (
                <>
                    <div
                        className={style.mobileBackdrop}
                        onClick={() => setMobileOpen(false)}
                        aria-hidden="true"
                    />
                    <nav
                        id="mobile-nav"
                        className={style.mobilePanel}
                        aria-label="Hovedmeny"
                    >
                        <Grid container direction="column" className={style.mobileItems}>
                            {items}
                        </Grid>
                    </nav>
                </>
            )}
        </div>
    );
}

export default Header;
